import React from 'react';
import { Box, Typography, Chip, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { tokens } from '../../theme';
import CoordinateGrid from './CoordinateGrid';
import { defaultCoordinateConfig, formatElementSummary } from './coordinateUtils';

/**
 * Read-Only Component for Viewing Submitted Coordinate Answers
 * Shows student answer with optional correct answer overlay
 */
const CoordinateGridReadOnly = ({
  config = defaultCoordinateConfig,
  studentAnswer = { elements: [] },
  correctAnswer = null,
  isCorrect = null,
  showCorrect = true, // Whether to show correct answer (for teachers/admins)
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const gridConfig = config.grid || config; // Support both nested and flat config

  const studentElements = studentAnswer?.elements || [];
  const correctElements = correctAnswer?.elements || [];

  return (
    <Box sx={{ width: '100%', maxWidth: 450 }}>
      {/* Result indicator */}
      {isCorrect !== null && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            p: 1,
            borderRadius: 1,
            backgroundColor: isCorrect
              ? 'rgba(76, 206, 172, 0.1)'
              : 'rgba(244, 67, 54, 0.1)',
          }}
        >
          {isCorrect ? (
            <>
              <CheckCircleIcon sx={{ color: colors.greenAccent[500] }} />
              <Typography sx={{ color: colors.greenAccent[400] }}>Correct!</Typography>
            </>
          ) : (
            <>
              <CancelIcon sx={{ color: colors.redAccent[500] }} />
              <Typography sx={{ color: colors.redAccent[400] }}>Incorrect</Typography>
            </>
          )}
        </Box>
      )}

      {/* Grid */}
      <CoordinateGrid
        config={gridConfig}
        elements={studentElements}
        readOnly={true}
        highlightElements={showCorrect && !isCorrect ? correctElements : []}
        width={400}
        height={400}
        showCursor={false}
      />

      {/* Legend */}
      {showCorrect && !isCorrect && correctElements.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: colors.blueAccent[500],
              }}
            />
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              Your answer
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: colors.greenAccent[500],
              }}
            />
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              Correct answer
            </Typography>
          </Box>
        </Box>
      )}

      {/* Student Answer Summary */}
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 0.5 }}>
          Your answer ({studentElements.length} element{studentElements.length !== 1 ? 's' : ''}):
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {studentElements.length === 0 ? (
            <Typography variant="caption" sx={{ color: colors.grey[500], fontStyle: 'italic' }}>
              No answer submitted
            </Typography>
          ) : (
            studentElements.map((el, i) => (
              <Chip
                key={el.id || i}
                label={formatElementSummary(el)}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: colors.blueAccent[500],
                  color: colors.grey[200],
                  fontSize: '0.7rem',
                }}
              />
            ))
          )}
        </Box>
      </Box>

      {/* Correct Answer Summary (for teachers/admins when student got it wrong) */}
      {showCorrect && !isCorrect && correctElements.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 0.5 }}>
            Correct answer ({correctElements.length} element
            {correctElements.length !== 1 ? 's' : ''}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {correctElements.map((el, i) => (
              <Chip
                key={el.id || i}
                label={formatElementSummary(el)}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: colors.greenAccent[500],
                  color: colors.grey[200],
                  fontSize: '0.7rem',
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CoordinateGridReadOnly;
